import type { Layer } from '@pn/core/domain/layer';
import { isInteractableLayer } from '@pn/core/domain/layer';
import {
  generatePatternLayerId,
  getPointsLayerId,
  getSelectedLayerId,
} from '@pn/core/domain/workspace';
import { isDevelopment } from '@pn/core/utils/env';
import { toWebGLRGBA } from '@pn/services/color/webglColor';
import type { MapboxIMap } from '@pn/services/map/mapbox';
import { customLayersRegistry } from '@pn/services/map/mapbox/custom/registry';
import {
  mapboxLayerStyleMapper,
  type MapboxStyle,
} from '@pn/services/map/mapbox/mappers/mapboxLayerStyleMapper';
import { hashObject } from '@pn/services/utils/hash';
import { isNil } from 'lodash-es';
import mapboxgl from 'mapbox-gl';

const cache = new Map<string, number>();

export const updateDataLayerStyle: MapboxIMap['updateDataLayerStyle'] =
  function (this: MapboxIMap, layer, { style = {}, selectionStyle = {} }) {
    if (!this.hasLayer(layer.id)) {
      throw new Error(`Layer ${layer.id} does not exist`);
    }

    const hash = hashObject(style);
    if (cache.get(layer.id) === hash) return;

    const mapboxStyle = mapboxLayerStyleMapper.toTargetLayerStyle(
      style,
      layer.type
    );
    applyMapboxStyle(this._native, layer.id, mapboxStyle);

    if (isInteractableLayer(layer)) {
      const mapboxSelectionStyle = mapboxLayerStyleMapper.toTargetLayerStyle(
        selectionStyle,
        layer.type
      );
      applyMapboxStyle(
        this._native,
        getSelectedLayerId(layer.id),
        mapboxSelectionStyle
      );
    }

    if (layer.renderAsPoints && !isNil(style.color)) {
      this._native.setPaintProperty(
        getPointsLayerId(layer.id),
        'circle-color',
        style.color
      );
      this._native.setPaintProperty(
        getSelectedLayerId(getPointsLayerId(layer.id)),
        'circle-color',
        selectionStyle.color
      );
    }

    cache.set(layer.id, hash);
  };

function applyMapboxStyle(
  mapboxMap: mapboxgl.Map,
  layerId: Layer['id'],
  mapboxStyle: MapboxStyle
): void {
  const patternLayerId = generatePatternLayerId(layerId);
  if (
    customLayersRegistry.has(patternLayerId) &&
    mapboxStyle.paint &&
    'fill-color' in mapboxStyle.paint
  ) {
    const customLayer = customLayersRegistry.get(patternLayerId)!;
    const color = mapboxStyle.paint['fill-color'];
    customLayer.setPatternProps({
      color: toWebGLRGBA(color),
    });
    return;
  }

  if (!isNil(mapboxStyle.layout)) {
    for (const [property, value] of Object.entries(mapboxStyle.layout)) {
      mapboxMap.setLayoutProperty(layerId, property, value, {
        validate: isDevelopment(),
      });
    }
  }

  if (!isNil(mapboxStyle.paint)) {
    for (const [property, value] of Object.entries(mapboxStyle.paint)) {
      mapboxMap.setPaintProperty(layerId, property, value, {
        validate: isDevelopment(),
      });
    }
  }
}
